import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PayMayaService } from './paymaya.service';
import { Observable } from 'rxjs';
import firebase from 'firebase';
import Swal from 'sweetalert2';

interface GeotagPhoto {
  id: string;
  geotagImage: string;
  plateNumber: string;
  address: string;
  lat: number;
  lng: number;
  timestamp: any;
  accuracy?: number;
  gateKeeperInfo?: any;
}

export interface ParkingRecord {
  id: string;
  uid?: string;
  user_id?: string;
  code?: string;
  account_key?: string;
  plate_number: string;
  vehicle_type: string;
  vehicle_type_custom_name?: string;
  vehicle_model?: string;
  vehicle_color?: string;
  geotag?: GeotagPhoto | any;
  transaction_type?: number;
  parking_duration?: number;
  entry_status?: boolean;
  date_registered?: any;
  date_updated?: any;
  created_at?: any;
  timestamp?: any;
  time_in: string;
  time_out?: string;
  payment_action_type?: number;
  ticket_number?: string;
  processedBy?: string;
  processedBy_id?: string;
  zone_name: string;
  zone_id?: string;
  original_amount_due?: number;
  noplate?: string;
  unpaid?: string;
  zone_slot_assignment?: number;
  auto_end_time?: string | Date | any;
  actual_end_time?: string | Date | any;
  selected_duration?: number;
  duration_hours?: number;
  initial_payment?: number;
  additional_payment?: number;
  calculated_for_duration?: number;
  is_manual_assignment?: boolean;
  slot_id?: string;
  slot_name: string;
  slot_code?: string;
  slot_arrangement?: number;
  updated_at?: any;
  additional_payment_recieved_date?: any;
  amount_paid?: number;
  amount_due_paid?: number;
  payment_id?: string;
  payment_status?: boolean | string;
  paid: boolean;
  additional_payment_recieved_by_id?: string;
  amount_due?: number;
  reference_number?: string;
  additional_payment_recieved_by?: string;
  payment_type?: string;
  discount_applied?: boolean;
  discounted_amount?: number;
  original_amount?: number;
  discount_details?: string;
  initial_payment_recieved_by_id?: string;
  initial_payment_recieved_by?: string;
  discount_percent?: number;
  discount_savings?: number;
  payment_date?: any;
  initial_payment_recieved_date?: any;
  allow_background_processing?: boolean;
  expired_at?: any;
  isExpired?: boolean;
  last_synced_at?: any;
  last_sync_status?: number;
  session_status_text?: string;
  auto_completed_at?: any;
  being_processed_by?: string;
  exit_status?: boolean;
  session_status: number;
  auto_completed?: boolean;
  status?: string;
  updatedAt?: any;
  remarks?: string;
  transaction_type_name?: string;
  date_paid?: string;
  calculatedAmount?: number;
  duration?: string;
}

export interface PaymentData {
  user_id?: string;
  plate_number?: string;
  plate_numbers?: string[];
  zone_name?: string;
  zone_names?: string;
  slot_name?: string;
  parking_session_id?: string;
  parking_session_ids?: string[];
  amount: number;
  currency: string;
  status: string;
  payment_status: string;
  created_at: Date;
  time_in?: string;
  time_out?: string;
  vehicle_type?: string;
  payment_method: string;
  session_count?: number;
  sessions_detail?: any[];
  checkout_id?: string;
  checkout_url?: string;
  maya_response?: any;
  transaction_reference?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PaymayaPaymentService {

  constructor(
    private http: HttpClient,
    private payMayaService: PayMayaService
  ) { }

  calculateParkingFee(record: ParkingRecord): Promise<number> {
    return new Promise((resolve) => {
      try {
        // Use existing amount_due if available
        if (record.amount_due && record.amount_due > 0) {
          resolve(record.amount_due);
          return;
        }

        // Fallback to amount_paid if no amount_due
        if (record.amount_paid && record.amount_paid > 0) {
          resolve(record.amount_paid);
          return;
        }

        // Final fallback to default minimum fee
        const defaultFee = 0;
        resolve(defaultFee);
        
      } catch (error) {
        console.error('Error calculating parking fee:', error);
        const fallbackFee = record.vehicle_type === 'motorcycle' ? 20 : 50;
        resolve(fallbackFee);
      }
    });
  }

  async calculateTotalUnpaidAmount(unpaidRecords: ParkingRecord[]): Promise<number> {
    let totalAmount = 0;
    
    for (const record of unpaidRecords) {
      const amount = await this.calculateParkingFee(record);
      totalAmount += amount;
    }
    
    return totalAmount;
  }

  shouldShowPayNowButton(record: ParkingRecord): boolean {
    return !record.paid && (record.amount_due || 0) > 0;
  }

  getEffectiveEndTime(record: ParkingRecord): string {
    // If there's auto_end_time and no time_out, use auto_end_time
    if (record.auto_end_time && !record.time_out) {
      return this.formatFirebaseTimestamp(record.auto_end_time);
    }
    // Otherwise use time_out or 'Active'
    return record.time_out || 'Active';
  }

  async getPaymentRecordBySessionId(sessionId: string): Promise<any> {
    try {
      // First query: Check for single payments using parking_session_id
      const singlePaymentQuery = await firebase.firestore()
        .collection('online_payments')
        .where('parking_session_id', '==', sessionId)
        .where('isPaid', '==', true)
        .orderBy('updated_at', 'desc')
        .limit(1)
        .get();

      if (!singlePaymentQuery.empty) {
        const doc = singlePaymentQuery.docs[0];
        const data = doc.data();
        
        return {
          id: doc.id,
          amount: parseFloat(data.amount || data.totalAmount?.value || 0),
          payment_method: data.payment_method || '-',
          payment_scheme: data.paymentScheme || '-',
          payment_status: data.status || data.paymentStatus || '-',
          date_paid: this.formatFirebaseTimestamp(data.updated_at || data.updatedAt || data.created_at),
          transaction_reference: data.transaction_reference || data.requestReferenceNumber,
          parking_session_ids: data.parking_session_id ? [data.parking_session_id] : [],
          fundSource: {
            type: data.fundSource?.type || '-',
            description: data.fundSource?.description || '-'
          },
          payment_type: data.payment_type || '-',
          is_bulk_payment: data.is_bulk_payment || false,
          session_count: data.session_count || 1,
          metadata: data.metadata || {}
        };
      }

      // Second query: Check for bulk payments using parking_session_ids array
      const bulkPaymentQuery = await firebase.firestore()
        .collection('online_payments')
        .where('parking_session_ids', 'array-contains', sessionId)
        .where('isPaid', '==', true)
        .orderBy('updated_at', 'desc')
        .limit(1)
        .get();

      if (!bulkPaymentQuery.empty) {
        const doc = bulkPaymentQuery.docs[0];
        const data = doc.data();
        
        // For bulk payments, calculate the per-session amount
        const sessionCount = data.session_count || (data.parking_session_ids?.length || 1);
        const totalAmount = parseFloat(data.amount || data.totalAmount?.value || 0);
        const perSessionAmount = sessionCount > 1 ? (totalAmount / sessionCount) : totalAmount;
        
        return {
          id: doc.id,
          amount: totalAmount,
          per_session_amount: perSessionAmount,
          payment_method: data.payment_method || '-',
          payment_scheme: data.paymentScheme || '-',
          date_paid: this.formatFirebaseTimestamp(data.updated_at || data.updatedAt || data.created_at),
          transaction_reference: data.transaction_reference || data.requestReferenceNumber,
          checkout_id: data.checkout_id || data.id,
          fundSource: {
            type: data.fundSource?.type || '-',
            description: data.fundSource?.description || '-'
          },
          payment_type: data.payment_type || '-',
          is_bulk_payment: true,
          session_count: sessionCount,
          parking_session_ids: data.parking_session_ids || [],
          sessions_detail: data.sessions_detail || [],
          metadata: data.metadata || {}
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching payment record:', error);
      return null;
    }
  }

  private formatFirebaseTimestamp(timestamp: any): string {
    if (!timestamp) return 'N/A';
    
    try {
      let date: Date;
      if (timestamp._seconds) {
        date = new Date(timestamp._seconds * 1000);
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp.toDate) {
        date = timestamp.toDate();
      } else {
        date = new Date(timestamp);
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  }

  async openSinglePaymentModal(record: ParkingRecord, user: any, redirectBase: string = '/myparking'): Promise<boolean> {
    try {
      const calculatedAmount = await this.calculateParkingFee(record);
      
      // Get payment record if available
      const paymentRecord = await this.getPaymentRecordBySessionId(record.id);
      
      // Get geotag photo if available
      const geotagPhoto = record.geotag;

      // Format geotag photo HTML
      const geotagHtml = geotagPhoto ? `
        <div class="mt-4 pt-4 border-t border-gray-200">
          <a href="${geotagPhoto.geotagImage}" target="_blank" rel="noopener noreferrer" class="block">
            <div class="bg-purple-50 rounded-lg p-3 border border-purple-200 hover:bg-purple-100 hover:border-purple-300 transition-all cursor-pointer">
              <div class="flex items-center space-x-3">
                <div class="flex-shrink-0">
                  <img src="${geotagPhoto.geotagImage}" alt="Geotag photo" class="w-16 h-16 object-cover rounded-lg border-2 border-transparent">
                </div>
                <div class="flex-1 ml-3">
                  <p class="text-sm font-medium text-purple-800">
                    <i class="fas fa-map-marker-alt mr-1"></i>Location Photo
                  </p>
                  <p class="text-xs text-purple-700 mt-1">${geotagPhoto.address || 'Location not available'}</p>
                  <p class="text-xs text-purple-600 mt-1">Lat: ${(geotagPhoto.lat || 0).toFixed(6)}, Lng: ${(geotagPhoto.lng || 0).toFixed(6)}</p>
                  <p class="text-xs text-purple-500 mt-1 italic">
                    <i class="fas fa-external-link-alt mr-1"></i>Click to view full size image
                  </p>
                </div>
              </div>
            </div>
          </a>
        </div>
      ` : '';
      
      const result = await Swal.fire({
        html: `
          <div class="text-left space-y-4 mobile-compact">
            <!-- Vehicle Details Section -->
            <div class="mb-2 card-container compact-section">
              <h4 class="text-xs font-semibold text-gray-800 mb-2 tracking-wide">
                <i class="fas fa-car mr-1 text-blue-600"></i>Vehicle Details
              </h4>
              <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div>
                  <span class="font-medium text-gray-600">Plate:</span>
                  <div class="text-gray-800 font-bold text-sm">${record.plate_number || '-'}</div>
                </div>
                <div>
                  <span class="font-medium text-gray-600">Type:</span>
                  <div class="text-gray-800 font-bold text-sm">${record.vehicle_type || '-'}</div>
                </div>
              </div>
            </div>

            <!-- Transaction Details Section -->
            <div class="mb-2 card-container compact-section">
              <h4 class="text-xs font-semibold text-gray-800 mb-2 tracking-wide">
                <i class="fas fa-clock mr-1 text-gray-600"></i>Transaction Details
              </h4>
              <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div>
                  <span class="font-medium text-gray-600">Start:</span>
                  <div class="text-gray-800 font-bold text-sm">${record.time_in || '-'}</div>
                </div>
                <div>
                  <span class="font-medium text-gray-600">End:</span>
                  <div class="text-gray-800 font-bold text-sm">${record.time_out || this.formatFirebaseTimestamp(record.actual_end_time) || this.formatFirebaseTimestamp(record.auto_end_time) || '-'}</div>
                </div>
                <div>
                  <span class="font-medium text-gray-600">Duration:</span>
                  <div class="text-gray-800 font-bold text-sm">${record.duration_hours ? record.duration_hours + 'hr' : ''}${record.duration ? record.duration.replace(/hr.*/, '') : ''}</div>
                </div>
                ${user && user.uid ? `
                  <div>
                    <span class="font-medium text-gray-600">Location:</span>
                    <div class="text-gray-800 font-bold text-sm">${record.zone_name || '-'}</div>
                  </div>
                  <div>
                    <span class="font-medium text-gray-600">Slot:</span>
                    <div class="text-gray-800 font-bold text-sm">${record.slot_name || '-'}</div>
                  </div>
                  <div>
                    <span class="font-medium text-gray-600">Attendant:</span>
                    <div class="text-gray-800 font-bold text-sm">${record.processedBy || 'System'}</div>
                  </div>
                ` : ''}
              </div>
            </div>

            <!-- Pay Parking Fee Section -->
            <div class="card-container mb-2 compact-section">
              <h4 class="text-xs font-semibold text-gray-800 mb-2 tracking-wide">
                <i class="fas fa-money-bill-wave mr-1 text-yellow-600"></i>Payment Details
              </h4>
              <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div>
                  <span class="font-medium text-gray-600">Amount due:</span>
                  <div class="font-bold text-sm">₱${record.amount_due || 0}</div>
                </div>
                <div>
                  <span class="font-medium text-gray-600">Status:</span>
                  <div class="font-bold text-sm ${record.paid ? 'text-green-600' : 'text-red-600'}">${record.paid ? 'Paid' : 'Unpaid'}</div>
                </div>
                <div>
                  <span class="font-medium text-gray-600">Hours:</span>
                  <div class="text-gray-800 font-bold text-sm">${record.duration_hours || record.parking_duration || '0'}hr/s</div>
                </div>
                <div>
                  <span class="font-medium text-gray-600">Amount paid:</span>
                  <div class="text-green-600 font-bold text-sm">₱${record.amount_paid || 0}</div>
                </div>
              </div>

              ${record.initial_payment || record.additional_payment !== undefined ? `
                <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mt-2">
                  ${record.initial_payment ? `
                    <div>
                      <span class="font-medium text-gray-600">Initial rate:</span>
                      <div class="text-gray-800 font-bold text-sm">₱${record.initial_payment}</div>
                    </div>
                  ` : ''}
                  ${record.additional_payment !== undefined && record.additional_payment >= 0 ? `
                    <div>
                      <span class="font-medium text-gray-600">Additional:</span>
                      <div class="text-gray-800 font-bold text-sm">₱${record.additional_payment}</div>
                    </div>
                  ` : ''}
                </div>
              ` : ''}

              ${record.discount_applied && record.discount_details ? `
                <div class="mt-2 p-2 bg-blue-50 rounded text-xs">
                  <span class="font-bold text-blue-800">${record.discount_details}</span>
                  <div class="mt-1">
                    <span class="text-gray-600">Original: ₱${record.original_amount}</span> → 
                    <span class="text-blue-600 font-bold">₱${record.discounted_amount}</span>
                  </div>
                </div>
              ` : ''}
            </div>

            ${paymentRecord ? `
              <!-- Payment Record Section -->
              <div class="card-container mb-3">
                <h4 class="text-sm font-bold text-gray-800 mb-3 tracking-wide">
                  <i class="fas fa-receipt mr-2 text-green-600"></i>Payment Record
                </h4>
                <div class="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div>
                    <span class="font-medium text-gray-600">Transaction reference:</span>
                    <div class="text-gray-800 font-bold ">${paymentRecord.transaction_reference || '-'}</div>
                  </div>
                  <div>
                    <span class="font-medium text-gray-600">Payment scheme:</span>
                    <div class="text-gray-800 font-bold ">${paymentRecord.fundSource?.type || (paymentRecord.payment_scheme).toUpperCase()}</div>
                  </div>
                  <div>
                    <span class="font-medium text-gray-600">Payment method:</span>
                    <div class="text-gray-800 font-bold ">${(paymentRecord.payment_method).toUpperCase()}</div>
                  </div>
                  ${paymentRecord.is_bulk_payment ? `
                    <div>
                      <span class="font-medium text-gray-600">Session amount:</span>
                      <div class="text-green-600 font-bold">₱${paymentRecord.per_session_amount?.toFixed(2) || 0}</div>
                    </div>
                    <div>
                      <span class="font-medium text-gray-600">Total bulk amount:</span>
                      <div class="text-blue-600 font-bold">₱${paymentRecord.amount || 0}</div>
                    </div>
                  ` : `
                    <div>
                      <span class="font-medium text-gray-600">Amount paid:</span>
                      <div class="text-green-600 font-bold">₱${paymentRecord.amount || 0}</div>
                    </div>
                  `}
                  <div>
                    <span class="font-medium text-gray-600">Payment status:</span>
                    <div class="text-green-600 font-bold">${paymentRecord.payment_status || '-'}</div>
                  </div>
                  <div>
                    <span class="font-medium text-gray-600">Date paid:</span>
                    <div class="text-gray-800 font-bold">${paymentRecord.date_paid || '-'}</div>
                  </div>
                  ${paymentRecord.parking_session_ids && paymentRecord.parking_session_ids.length > 0 ? `
                    <div class="col-span-2">
                      <span class="font-medium text-gray-600">Parking session ID${paymentRecord.parking_session_ids.length > 1 ? 's' : ''}:</span>
                      <div class="text-gray-800 font-bold text-sm mt-1">
                        ${paymentRecord.parking_session_ids.length === 1 ? 
                          `<div class="break-all">${paymentRecord.parking_session_ids[0]}</div>` :
                          `<ul class="list-disc list-inside space-y-1">
                            ${paymentRecord.parking_session_ids.map(id => `<li class="break-all">${id}</li>`).join('')}
                          </ul>`
                        }
                      </div>
                    </div>
                  ` : ''}
                </div>
              </div>
            ` : ''}

            ${record.paid ? `
              <!-- Payment Completed Section -->
              <div style="background: #e8f5e8; padding: 12px; border-radius: 6px; margin-bottom: 8px; border: 1px solid #28a745;">
                <div style="text-align: center;">
                  <i class="fas fa-check-circle" style="font-size: 2rem; color: #28a745; margin-bottom: 6px;"></i>
                  <p style="margin: 0 0 4px 0; color: #155724; font-size: 14px; font-weight: bold;">Payment Completed</p>
                  <p style="margin: 0; font-size: 1.8rem; font-weight: bold; color: #28a745;">₱${record.amount_paid || calculatedAmount}</p>
                </div>
              </div>
            ` : `
              <!-- Amount to Pay Highlight Section -->
              <div style="text-align: center; background: #f8f9fa; padding: 12px; border-radius: 6px; margin-bottom: 8px; border: 2px solid #4829AA;">
                <p style="margin: 0 0 4px 0; color: black; font-size: 12px;">Amount to Pay</p>
                <p style="margin: 0; font-size: 2rem; font-weight: bold; color: #4829AA;">₱${calculatedAmount}</p>
              </div>
              
              <div class="text-center pt-2">
                <p class="text-gray-600 text-xs mb-2">
                    <i class="fas fa-shield-alt mr-1"></i>Secure payment powered by
                </p>
                <img src="assets/img/Maya_logo.png" alt="Paymaya" style="width: 80px;" class="mx-auto">
              </div>
            `}

            ${geotagHtml ? `
              <div class="card-container mb-2 compact-section">
                <h4 class="text-xs font-semibold text-gray-800 mb-2 tracking-wide">
                  <i class="fas fa-camera mr-1 text-purple-600"></i>Location Photo
                </h4>
                <div class="text-gray-500">
                  ${geotagHtml}
                </div>
              </div>
            ` : ''}
          </div>
        `,
        showCancelButton: record.paid ? false : true,
        confirmButtonText: record.paid ? 'Close' : 'Pay Now',
        cancelButtonText: record.paid ? undefined : 'Cancel',
        confirmButtonColor: record.paid ? '#9CA3AF' : '#4829AA',
        cancelButtonColor: '#9CA3AF',
        width: '800px',
        allowEscapeKey: true,
        allowOutsideClick: true,
        showCloseButton: true,
        showLoaderOnConfirm: record.paid ? false : true,
        position: "top",
        didOpen: () => {
          // Add mobile back button support and ESC key functionality
          this.setupModalCloseHandlers();
          // Apply mobile-specific styling
          this.applyMobileStyling();
        },
        preConfirm: async () => {
          if (record.paid) {
            return true; // Just close the modal if already paid
          }
          try {
            return await this.processSinglePayment(record, user, calculatedAmount, redirectBase);
          } catch (error) {
            Swal.showValidationMessage(
              `Payment failed: ${error.message || 'Unknown error'}`
            );
            return false;
          }
        }
      });
      
      return result.isConfirmed && result.value;
      
    } catch (error) {
      console.error('Error in openSinglePaymentModal:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to open payment modal. Please try again.',
        icon: 'error',
        confirmButtonColor: '#2196f3'
      });
      return false;
    }
  }

  private async processSinglePayment(record: ParkingRecord, user: any, amount: number, redirectBase: string): Promise<boolean> {
    try {
      if (amount <= 0) {
        throw new Error('Unable to calculate parking fee. Please try again.');
      }

      const invoiceData: PaymentData = {
        user_id: user?.uid,
        plate_number: record.plate_number,
        zone_name: record.zone_name,
        slot_name: record.slot_name,
        parking_session_id: record.id,
        amount: amount,
        currency: 'PHP',
        status: 'PENDING',
        payment_status: 'PENDING',
        created_at: new Date(),
        time_in: record.time_in,
        time_out: record.time_out,
        vehicle_type: record.vehicle_type,
        payment_method: 'maya'
      };

      const checkoutPayload = this.generateMayaCheckoutPayload(invoiceData, user, redirectBase);
      
      if (!checkoutPayload) {
        throw new Error('Unable to generate payment details. Please try again.');
      }

      const checkoutResponse: any = await this.payMayaService.createCheckout(checkoutPayload).toPromise();
      
      const redirectUrl = checkoutResponse?.redirectUrl || checkoutResponse?.checkoutUrl;
      const checkoutId = checkoutResponse?.checkoutId;
      
      if (checkoutResponse && redirectUrl && checkoutId) {
        await this.storePaymentRecord({
          ...invoiceData,
          checkout_id: checkoutId,
          checkout_url: redirectUrl,
          maya_response: checkoutResponse,
          transaction_reference: this.generatePaymentReference(record.id)
        });

        localStorage.setItem('lastPaymentAttempt', JSON.stringify({
          checkout_id: checkoutId,
          parking_session_id: record.id,
          plate_number: record.plate_number,
          zone_name: record.zone_name,
          amount: amount,
          timestamp: new Date().toISOString(),
          user_id: user?.uid,
          transaction_reference: this.generatePaymentReference(record.id)
        }));

        window.location.href = redirectUrl;
        return true;
      } else {
        throw new Error('Invalid checkout response from Maya: ' + JSON.stringify(checkoutResponse));
      }

    } catch (error) {
      console.error('Payment processing error:', error);
      throw error;
    }
  }

  private generateMayaCheckoutPayload(invoiceData: PaymentData, user: any, redirectBase: string): any {
    const userDisplayName = user?.displayName || user?.phoneNumber || 'Guest User';
    const [firstName, ...lastNameParts] = userDisplayName.split(' ');
    const lastName = lastNameParts.join(' ') || 'User';

    return {
      totalAmount: {
        value: invoiceData.amount,
        currency: 'PHP'
      },
      buyer: {
        firstName: firstName || 'Parkbot',
        lastName: lastName || 'User',
        contact: {
          phone: user?.phoneNumber || '',
          email: user?.email || ''
        }
      },
      items: [
        {
          name: `Parking Fee - ${invoiceData.zone_name}`,
          quantity: 1,
          totalAmount: {
            value: invoiceData.amount,
            currency: 'PHP'
          },
          description: `Parking session for ${invoiceData.plate_number} at ${invoiceData.zone_name}, Slot ${invoiceData.slot_name}`
        }
      ],
      reference: this.generatePaymentReference(invoiceData.parking_session_id || ''),
      redirectUrl: {
        success: `${window.location.origin}/payment-status/success`,
        failure: `${window.location.origin}/payment-status/failure`,
        cancel: `${window.location.origin}/payment-status/cancel`
      },
      metadata: {
        user_id: user?.uid || 'guest',
        parking_session_id: invoiceData.parking_session_id,
        plate_number: invoiceData.plate_number,
        zone_name: invoiceData.zone_name,
        payment_source: redirectBase.includes('mystreetparking') ? 'street_parking_search' : 'dashboard'
      }
    };
  }

  private async storePaymentRecord(paymentData: PaymentData): Promise<void> {
    try {
      const cleanedData = this.cleanPaymentDataForFirestore(paymentData);
      
      await firebase.firestore().collection('online_payments').add({
        ...cleanedData,
        payment_method: 'maya',
        created_at: firebase.firestore.FieldValue.serverTimestamp(),
        updated_at: firebase.firestore.FieldValue.serverTimestamp()
      });
    } catch (error) {
      console.error('Error storing payment record:', error);
      throw error;
    }
  }

  private cleanPaymentDataForFirestore(data: any): any {
    const cleaned: any = {};
    
    Object.keys(data).forEach(key => {
      const value = data[key];
      
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          cleaned[key] = value.filter(item => item !== undefined && item !== null);
        } else if (typeof value === 'object' && value.constructor === Object) {
          const cleanedObj = this.cleanPaymentDataForFirestore(value);
          if (Object.keys(cleanedObj).length > 0) {
            cleaned[key] = cleanedObj;
          }
        } else if (typeof value === 'object' && value._seconds) {
          cleaned[key] = value;
        } else if (value instanceof Date) {
          cleaned[key] = firebase.firestore.Timestamp.fromDate(value);
        } else {
          cleaned[key] = value;
        }
      }
    });
    
    return cleaned;
  }

  async openBulkPaymentModal(unpaidRecords: ParkingRecord[], user: any, redirectBase: string = '/myparking'): Promise<boolean> {
    try {
      if (!unpaidRecords || unpaidRecords.length === 0) {
        throw new Error('No unpaid records found.');
      }

      const totalAmount = await this.calculateTotalUnpaidAmount(unpaidRecords);
      
      const result = await Swal.fire({
        title: 'Pay All Unpaid Parking Fees',
        html: `
          <div class="text-left mobile-compact" style="margin: 12px 0;">
            <div style="background: #f8f9fa; padding: 12px; border-radius: 6px; margin-bottom: 8px;">
              <h4 style="margin: 0 0 8px 0; color: #333; font-size: 16px;">Pay All Unpaid Fees</h4>
              <div style="font-size: 12px; line-height: 1.4;">
                <p style="margin: 2px 0;"><strong>Plate:</strong> ${unpaidRecords[0].plate_number}</p>
                <p style="margin: 2px 0;"><strong>Sessions:</strong> ${unpaidRecords.length} unpaid</p>
                <p style="margin: 2px 0;"><strong>Zones:</strong> ${[...new Set(unpaidRecords.map(r => r.zone_name))].join(', ')}</p>
              </div>
            </div>
            
            <div style="background: #f8f9fa; padding: 12px; border-radius: 6px; margin-bottom: 8px; text-align: center; border: 2px solid #4829AA;">
              <p style="margin: 0 0 4px 0; color: black; font-size: 12px;">Total Amount to Pay</p>
              <p style="margin: 0; font-size: 2rem; font-weight: bold; color: #4829AA;">₱${totalAmount}</p>
            </div>
            
            <div style="background: #e8f5e8; padding: 8px; border-radius: 6px; border: 1px solid #d4edda;">
              <div style="display: flex; align-items: center; font-size: 12px; color: #3a6b3a;">
                <i class="fas fa-shield-alt" style="margin-right: 6px;"></i>
                <div>
                  <p style="margin: 0; font-weight: 600;">Secure Payment</p>
                  <p style="margin: 0;">Redirects to Maya payment gateway</p>
                </div>
              </div>
            </div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Pay All with Maya',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#4829AA',
        cancelButtonColor: '#9CA3AF',
        width: '680px',
        allowEscapeKey: true,
        allowOutsideClick: true,
        showCloseButton: true,
        showLoaderOnConfirm: true,
        didOpen: () => {
          // Add mobile back button support and ESC key functionality
          this.setupModalCloseHandlers();
          // Apply mobile-specific styling
          this.applyMobileStyling();
        },
        preConfirm: async () => {
          try {
            return await this.processBulkPayment(unpaidRecords, user, totalAmount, redirectBase);
          } catch (error) {
            Swal.showValidationMessage(
              `Payment failed: ${error.message || 'Unknown error'}`
            );
            return false;
          }
        }
      });
      
      return result.isConfirmed && result.value;
      
    } catch (error) {
      console.error('Error in openBulkPaymentModal:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to open payment modal. Please try again.',
        icon: 'error',
        confirmButtonColor: '#2196f3'
      });
      return false;
    }
  }

  private async processBulkPayment(unpaidRecords: ParkingRecord[], user: any, totalAmount: number, redirectBase: string): Promise<boolean> {
    try {
      if (totalAmount <= 0) {
        throw new Error('Unable to calculate total parking fee. Please try again.');
      }

      const invoiceData: PaymentData = {
        user_id: user?.uid,
        plate_number: unpaidRecords[0].plate_number,
        plate_numbers: unpaidRecords.map(r => r.plate_number),
        zone_names: [...new Set(unpaidRecords.map(r => r.zone_name))].join(', '),
        parking_session_ids: unpaidRecords.map(r => r.id),
        amount: totalAmount,
        currency: 'PHP',
        status: 'PENDING',
        payment_status: 'PENDING',
        created_at: new Date(),
        vehicle_type: unpaidRecords[0].vehicle_type,
        payment_method: 'maya',
        session_count: unpaidRecords.length,
        sessions_detail: unpaidRecords.map(record => ({
          id: record.id,
          plate_number: record.plate_number,
          zone_name: record.zone_name,
          slot_name: record.slot_name,
          time_in: record.time_in,
          time_out: record.time_out || this.getEffectiveEndTime(record),
          vehicle_type: record.vehicle_type,
          amount_due: record.amount_due || 0
        }))
      };

      const checkoutPayload = this.generateBulkMayaCheckoutPayload(invoiceData, user, redirectBase);
      
      if (!checkoutPayload) {
        throw new Error('Unable to generate payment details. Please try again.');
      }

      const checkoutResponse: any = await this.payMayaService.createCheckout(checkoutPayload).toPromise();
      
      const redirectUrl = checkoutResponse?.redirectUrl || checkoutResponse?.checkoutUrl;
      const checkoutId = checkoutResponse?.checkoutId;
      
      if (checkoutResponse && redirectUrl && checkoutId) {
        await this.storePaymentRecord({
          ...invoiceData,
          checkout_id: checkoutId,
          checkout_url: redirectUrl,
          maya_response: checkoutResponse,
          transaction_reference: this.generatePaymentReference(unpaidRecords.map(r => r.id).join('-'))
        });

        localStorage.setItem('lastPaymentAttempt', JSON.stringify({
          checkout_id: checkoutId,
          parking_session_ids: unpaidRecords.map(r => r.id),
          plate_numbers: unpaidRecords.map(r => r.plate_number),
          zone_names: [...new Set(unpaidRecords.map(r => r.zone_name))].join(', '),
          amount: totalAmount,
          timestamp: new Date().toISOString(),
          user_id: user?.uid,
          transaction_reference: this.generatePaymentReference(unpaidRecords.map(r => r.id).join('-')),
          is_bulk: true,
          session_count: unpaidRecords.length
        }));

        window.location.href = redirectUrl;
        return true;
      } else {
        throw new Error('Invalid checkout response from Maya: ' + JSON.stringify(checkoutResponse));
      }

    } catch (error) {
      console.error('Bulk payment processing error:', error);
      throw error;
    }
  }

  private generateBulkMayaCheckoutPayload(invoiceData: PaymentData, user: any, redirectBase: string): any {
    const userDisplayName = user?.displayName || user?.phoneNumber || 'Guest User';
    const [firstName, ...lastNameParts] = userDisplayName.split(' ');
    const lastName = lastNameParts.join(' ') || 'User';

    return {
      totalAmount: {
        value: invoiceData.amount,
        currency: 'PHP'
      },
      buyer: {
        firstName: firstName || 'Parkbot',
        lastName: lastName || 'User',
        contact: {
          phone: user?.phoneNumber || '',
          email: user?.email || ''
        }
      },
      items: [
        {
          name: `Parking Fees - ${invoiceData.session_count} Sessions`,
          quantity: invoiceData.session_count || 1,
          totalAmount: {
            value: invoiceData.amount,
            currency: 'PHP'
          },
          description: `Bulk payment for ${invoiceData.session_count} parking sessions for ${invoiceData.plate_number} across zones: ${invoiceData.zone_names}`
        }
      ],
      reference: this.generatePaymentReference(invoiceData.parking_session_ids?.join('-') || ''),
      redirectUrl: {
        success: `${window.location.origin}/payment-status/success`,
        failure: `${window.location.origin}/payment-status/failure`,
        cancel: `${window.location.origin}/payment-status/cancel`
      },
      metadata: {
        user_id: user?.uid || 'guest',
        parking_session_ids: invoiceData.parking_session_ids,
        plate_numbers: invoiceData.plate_numbers,
        zone_names: invoiceData.zone_names,
        payment_source: redirectBase.includes('myparking') ? 'street_parking_search' : 'dashboard',
        is_bulk_payment: true,
        session_count: invoiceData.session_count
      }
    };
  }

  private generatePaymentReference(sessionId: string): string {
    const timestamp = Date.now().toString();
    const shortTimestamp = timestamp.slice(-8);
    
    let shortSessionId = '';
    if (sessionId && sessionId.length > 0) {
      shortSessionId = sessionId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase();
    }
    
    if (shortSessionId.length === 0) {
      shortSessionId = Math.random().toString(36).substring(2, 10).toUpperCase();
    }
    
    const reference = `PARK-${shortSessionId}-${shortTimestamp}`;
    return reference.length > 36 ? reference.substring(0, 36) : reference;
  }

  private setupModalCloseHandlers(): void {
    // ESC key handler
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        Swal.close();
      }
    };

    // Mobile back button handler
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      Swal.close();
      // Push a new state to prevent actual navigation
      window.history.pushState(null, '', window.location.href);
    };

    // Click handler for mobile back button (pseudo-element)
    const handleBackButtonClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target && target.closest('.swal2-popup')) {
        const rect = target.getBoundingClientRect();
        const x = event.clientX;
        const y = event.clientY;
        
        // Check if click is in the back button area (top-left corner)
        if (x >= 16 && x <= 48 && y >= 16 && y <= 48) {
          event.preventDefault();
          event.stopPropagation();
          Swal.close();
        }
      }
    };

    // Add event listeners
    document.addEventListener('keydown', handleEscKey);
    
    // For mobile back button, push a state when modal opens
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    // Add click listener for mobile back button
    const swalPopup = Swal.getPopup();
    if (swalPopup) {
      swalPopup.addEventListener('click', handleBackButtonClick);
    }

    // Clean up event listeners when modal closes - Override the close button to clean up handlers
    const closeButtons = Swal.getPopup()?.querySelectorAll('.swal2-close, .swal2-cancel');
    closeButtons?.forEach(button => {
      button.addEventListener('click', () => {
        this.cleanupModalHandlers(handleEscKey, handlePopState, handleBackButtonClick);
      });
    });

    // Also cleanup on backdrop click and ESC
    const swalContainer = document.querySelector('.swal2-container');
    if (swalContainer) {
      swalContainer.addEventListener('click', (event) => {
        if (event.target === swalContainer) {
          this.cleanupModalHandlers(handleEscKey, handlePopState, handleBackButtonClick);
        }
      });
    }
  }

  private cleanupModalHandlers(
    escHandler: (event: KeyboardEvent) => void, 
    popStateHandler: (event: PopStateEvent) => void, 
    backButtonHandler?: (event: MouseEvent) => void
  ): void {
    // Remove event listeners
    document.removeEventListener('keydown', escHandler);
    window.removeEventListener('popstate', popStateHandler);
    
    // Remove back button click listener
    if (backButtonHandler) {
      const swalPopup = Swal.getPopup();
      if (swalPopup) {
        swalPopup.removeEventListener('click', backButtonHandler);
      }
    }
    
    // Remove the pushed state
    try {
      window.history.back();
    } catch (e) {
      // Ignore errors if history manipulation fails
    }
  }

  private applyMobileStyling(): void {
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      // Mobile styling
      const swalActions = document.querySelector('.swal2-actions') as HTMLElement;
      if (swalActions) {
        // Force mobile styling by removing inline styles that conflict
        swalActions.style.cssText = `
          position: fixed !important;
          bottom: 0 !important;
          left: 0 !important;
          right: 0 !important;
          width: 100% !important;
          background-color: #ffffff !important;
          border-top: 1px solid #e5e7eb !important;
          padding: 16px 20px 20px 20px !important;
          margin: 0 !important;
          box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1) !important;
          z-index: 10000 !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: center !important;
          align-items: stretch !important;
          gap: 12px !important;
        `;
      }

      // Style the confirm button for mobile
      const confirmButton = document.querySelector('.swal2-confirm') as HTMLElement;
      if (confirmButton) {
        confirmButton.style.cssText = `
          width: 100% !important;
          max-width: none !important;
          padding: 18px 24px !important;
          font-size: 1.1rem !important;
          font-weight: 600 !important;
          border-radius: 8px !important;
          margin: 0 !important;
          text-transform: none !important;
          background-color: #4829AA !important;
          border: none !important;
          color: white !important;
          order: 1 !important;
          display: block !important;
        `;
      }

      // Style the cancel button for mobile (show for bulk payment, hide for single payment)
      const cancelButton = document.querySelector('.swal2-cancel') as HTMLElement;
      if (cancelButton) {
        const isBulkPayment = document.querySelector('.swal2-title')?.textContent?.includes('Pay All');
        cancelButton.style.cssText = `
          width: 100% !important;
          max-width: none !important;
          padding: 14px 20px !important;
          font-size: 0.9rem !important;
          font-weight: 500 !important;
          border-radius: 8px !important;
          margin: 0 !important;
          background-color: #f3f4f6 !important;
          border: 1px solid #d1d5db !important;
          color: #374151 !important;
          order: 2 !important;
          display: ${isBulkPayment ? 'block' : 'none'} !important;
        `;
      }
      
      // Apply compact styling to modal content
      const modalContent = document.querySelector('.mobile-compact');
      if (modalContent) {
        (modalContent as HTMLElement).style.cssText = `
          font-size: 0.85rem !important;
          line-height: 1.3 !important;
        `;
      }
    } else {
      
      const swalActions = document.querySelector('.swal2-actions') as HTMLElement;
      if (swalActions) {
        // Force mobile styling by removing inline styles that conflict
        swalActions.style.cssText = `
          position: fixed !important;
          bottom: 0 !important;
          left: 0 !important;
          right: 0 !important;
          width: 100% !important;
          background-color: #ffffff !important;
          border-top: 1px solid #e5e7eb !important;
          padding: 16px 20px 20px 20px !important;
          margin: 0 !important;
          box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1) !important;
          z-index: 10000 !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: center !important;
          align-items: stretch !important;
          gap: 12px !important;
        `;
      }

      // Desktop styling - smaller buttons
      const confirmButton = document.querySelector('.swal2-confirm') as HTMLElement;
      if (confirmButton) {
        confirmButton.style.cssText = `
          width: 50% !important;
          max-width: 680px !important;
          padding: 12px 20px !important;
          font-size: 1rem !important;
          font-weight: 600 !important;
          border-radius: 8px !important;
          margin: 0px auto !important;
          text-transform: none !important;
          background-color: #4829AA !important;
          border: none !important;
          color: white !important;
        `;
      }

      const cancelButton = document.querySelector('.swal2-cancel') as HTMLElement;
      if (cancelButton) {
        const isBulkPayment = document.querySelector('.swal2-title')?.textContent?.includes('Pay All');
        cancelButton.style.cssText = `
          width: 50% !important;
          max-width: 680px !important;
          padding: 12px 20px !important;
          font-size: 1rem !important;
          font-weight: 500 !important;
          border-radius: 8px !important;
          margin: 0 auto !important;
          background-color: #f3f4f6 !important;myparking
          border: 1px solid #d1d5db !important;
          color: #374151 !important;
          display: ${isBulkPayment ? 'inline-block' : 'none'} !important;
        `;
      }
    }

    const closeButton = document.querySelector('.swal2-close') as HTMLElement;
    if (closeButton) {
      closeButton.style.cssText = `
          color: #374151 !important;
        `;
    }

    // Hide deny button if present (applies to both mobile and desktop)
    const denyButton = document.querySelector('.swal2-deny') as HTMLElement;
    if (denyButton) {
      denyButton.style.display = 'none !important';
    }
  }
}